import {Injectable} from '@angular/core';
import {BehaviorSubject, catchError, map, Observable, of, switchMap, take, tap} from 'rxjs';
import {LocalStorageService} from 'ngx-webstorage';
import {NGXLogger} from 'ngx-logger';
import {Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {HttpErrorResponse} from '@angular/common/http';
import {Backend, PERMISSION} from '@generated-backend-types';
import {TranslateService} from '@ngx-translate/core';
import {AuthenticationStateEnum} from '../enums/AuthenticationStateEnum';
import {AuthenticationService} from './api/authentication-service';
import {ModelPermission} from '../entities/model-permission.entity';
import AuthenticatedUser = Backend.App.Resources.Dashboard.Auth.AuthenticatedUser;
import DashboardLoginData = Backend.App.Data.Dashboard.Auth.DashboardLoginData;
import RoleEnum = Backend.App.Enums.Auth.RoleEnum;

@Injectable({providedIn: 'root'})
export class UserSessionService {
  public user$ = new BehaviorSubject<AuthenticatedUser | undefined | null>(undefined);
  public authState$ = new BehaviorSubject<AuthenticationStateEnum | undefined>(undefined);

  private readonly STORAGE_USER_NAME = 'user';

  public constructor(
    private authenticationService: AuthenticationService,
    private localStorageService: LocalStorageService,
    private log: NGXLogger,
    private router: Router,
    private toastr: ToastrService,
    private translate: TranslateService,
  ) {
    this.isLoggedIn().subscribe(value => {
      if (value === null || value === undefined) {
        this.clearUserCache();
      } else {
        this.user$.next(value);
      }
    });

    this.localStorageService.observe('logout').subscribe(() => {
      this.handleLogout();
      this.localStorageService.clear('logout');
    });

    this.localStorageService.observe('login')
      .pipe(switchMap(() => this.isLoggedIn()))
      .subscribe(user => {
        if (user) {
          this.handleLogin(user);
          this.router.navigate(['/']).then();
        }
      })
  }

  public loginUser(data: DashboardLoginData): Observable<AuthenticatedUser | null> {
    return this.authenticationService.login(data).pipe(
      take(1),
      map(res => {
        if (res.data.user) {
          this.handleLogin(res.data.user);
          this.localStorageService.store('login', new Date());
          return res.data.user;
        }

        if (res.data.two_factor) {
          this.authState$.next(AuthenticationStateEnum.LOGGED_IN_NOT_TWO_FACTOR_AUTHENTICATED);
          this.user$.next(null);
          this.router.navigate(['auth', 'two-factor', 'challenge']).then();
        }

        return null;
      }),
    );
  }

  public enableTwoFactorAuth(): Observable<{ svg: string }> {
    return this.authenticationService.enableTwoFactorAuth().pipe(
      switchMap(() => this.authenticationService.getTwoFactorAuthQrCode()),
    )
  }

  public confirmTwoFactorAuth(code: string) {
    return this.authenticationService.confirmTwoFactorAuth(code)
      .pipe(tap(() => this.authState$.next(AuthenticationStateEnum.AUTHENTICATED)));
  }

  public submitTwoFactorAuthChallenge(code: string): Observable<void> {
    return this.authenticationService.submitTwoFactorAuthChallenge(code).pipe(
      take(1),
      map(res => {
        if (res.data.user) {
          this.handleLogin(res.data.user);
          this.localStorageService.store('login', new Date());
          this.router.navigate(['']).then();
          return;
        }

        return;
      }),
    );
  }

  public handleLogin(user: AuthenticatedUser): void {
    this.authState$.next(user.two_factor_auth_enabled ? AuthenticationStateEnum.AUTHENTICATED : AuthenticationStateEnum.LOGGED_IN_TWO_FACTOR_AUTHENTICATION_NOT_ENABLED);
    this.user$.next(user);

    this.localStorageService.store(this.STORAGE_USER_NAME, {
      id: user.id,
      email: user.email,
      name: user.name,
      permissions: user.permissions,
      model_permissions: user.model_permissions,
      time_tracking_employee: user.time_tracking_employee,
      roles: user.roles,
    });
  }

  public getUserId(): Observable<string | undefined> {
    return of(this.localStorageService.retrieve(this.STORAGE_USER_NAME) as { id: string, email: string, name: string })
      .pipe(
        map(user => user && user.id ? user.id : undefined),
        catchError(() => of(undefined)),
      );
  }

  public getStoredUserInfo(): Observable<AuthenticatedUser | undefined> {
    return of(this.localStorageService.retrieve(this.STORAGE_USER_NAME) as AuthenticatedUser)
      .pipe(map(user => user ? user : undefined));
  }

  public logout(): Observable<void> {
    return this.authenticationService.logout().pipe(
      take(1),
      tap({
        next: () => {
          this.localStorageService.store('logout', true);
          this.handleLogout();
        },
        error: () => {
          this.handleLogout()
          this.log.warn('[auth] could not logout user')
        }
      }),
    );
  }

  public handleLogout(): void {
    this.clearUserCache();

    const notifyUser = this.user$.value !== undefined && this.user$.value !== null;

    this.user$.next(null);
    this.authState$.next(AuthenticationStateEnum.NOT_AUTHENTICATED);

    this.router.navigate(['auth', 'login'])
      .then(() => {
        if (notifyUser) this.toastr.success(this.translate.instant('misc.logged_out_message'));
      });
  }

  public hasAllPermission(permissions: (PERMISSION | ModelPermission)[]): boolean {
    return !this.userHasPermissions(permissions, this.getUserFromLocalStorage()).includes(false);
  }

  public hasAnyPermission(permissions: (PERMISSION | ModelPermission)[]): boolean {
    return this.userHasPermissions(permissions, this.getUserFromLocalStorage()).includes(true);
  }

  public hasAnyRole(roles: RoleEnum[]): boolean {
    const user = this.getUserFromLocalStorage();

    if (!user || !user.roles || user.roles.length === 0) return false;
    const userRoles = user.roles.map(role => role.key);

    for (const role of roles) {
      if (userRoles.includes(role)) {
        return true;
      }
    }

    return false;
  }

  public hasAllRoles(roles: RoleEnum[]): boolean {
    const user = this.getUserFromLocalStorage();

    if (!user || !user.roles || user.roles.length === 0) return false;
    const userRoles = user.roles.map(role => role.key);

    for (const role of roles) {
      if (! userRoles.includes(role)) {
        return false;
      }
    }

    return true;
  }

  private userHasPermissions(permissions: (PERMISSION | ModelPermission)[], user: AuthenticatedUser | undefined | null): boolean[] {
    if (!user) return permissions.map(() => false);

    return permissions.map((permission) => {
      let userPermissionsToCheck = user.permissions ?? [];
      let permissionToCheck = permission as string;

      if (typeof permission === 'object') {
        if (permission.modelId) {
          permissionToCheck = permission.permission.replace('*', permission.modelId);
          userPermissionsToCheck = user.model_permissions ?? [];
        } else {
          permissionToCheck = permission.permission;
        }
      }

      return userPermissionsToCheck.includes(permissionToCheck);
    });
  }

  private getUserFromLocalStorage(): AuthenticatedUser | undefined | null {
    return this.localStorageService.retrieve(this.STORAGE_USER_NAME) as AuthenticatedUser | undefined | null;
  }

  private isLoggedIn(): Observable<AuthenticatedUser | null> {
    return this.authenticationService.get().pipe(
      take(1),
      map(res => {
        this.handleLogin(res.data)
        return res.data;
      }),
      catchError(err => {
        if (err instanceof HttpErrorResponse) {
          if (err.status === 401) {
            this.handleLogout();
            return of(null);
          }
        }

        throw err;
      }),
    );
  }

  private clearUserCache(): void {
    this.localStorageService.clear(this.STORAGE_USER_NAME);
  }
}
